// Podpowiedź doboru sprzętu do wymiarów klienta (rezerwacja według klas w widgecie, 26.10.2026).
//
// Klient podaje w widgecie wzrost, wagę, rozmiar buta albo obwód głowy, a pracownik dobiera
// sztukę. Te reguły tylko USTAWIAJĄ KOLEJNOŚĆ listy i zaznaczają „pasuje”: decyzja należy do
// człowieka, więc reguły są proste, znane z wypożyczalni, i nie udają dokładności:
//   narty:      wzrost minus 15 cm (początkujący), 10 cm (średni), 5 cm (zaawansowany)
//   snowboard:  wzrost minus 20/15/10 cm
//   buty:       rozmiar EU; gdy sztuka ma rozmiar w mondopoint (np. 26.5), EU przeliczone ≈ EU·2/3 − 1
//   kijki:      wzrost × 0,68, zaokrąglone do 5 cm
//   kask:       obwód głowy (rozmiar sztuki „55-58” to zakres)
// Kategoria rozpoznawana po nazwie (PL i EN). Inna kategoria albo brak odpowiedzi: bez podpowiedzi,
// lista zostaje w kolejności klasy i nazwy.

const zawiera = (tekst, ...fragmenty) => fragmenty.some((f) => tekst.includes(f));

export function rodzajSprzetu(typeName) {
  const n = String(typeName || '').toLowerCase();
  // Kolejność ma znaczenie: „Buty snowboardowe” to buty, „Kijki narciarskie” to kijki.
  if (zawiera(n, 'but', 'boot')) return 'boots';
  if (zawiera(n, 'kij', 'pole')) return 'poles';
  if (zawiera(n, 'kask', 'helmet')) return 'helmet';
  if (zawiera(n, 'snowboard', 'deska', 'deski')) return 'snowboard';
  if (zawiera(n, 'nart', 'ski')) return 'ski';
  return null;
}

const ODJECIE = {
  ski: { beginner: 15, intermediate: 10, advanced: 5 },
  snowboard: { beginner: 20, intermediate: 15, advanced: 10 },
};

/** Docelowy rozmiar: `{ value, unit }` albo null, gdy brak danych do podpowiedzi. */
export function docelowyRozmiar(rodzaj, answers) {
  const a = answers || {};
  const wzrost = Number(a.height);
  if ((rodzaj === 'ski' || rodzaj === 'snowboard') && wzrost > 0) {
    const odj = ODJECIE[rodzaj][a.skill] ?? ODJECIE[rodzaj].intermediate;
    return { value: Math.round(wzrost - odj), unit: 'cm' };
  }
  if (rodzaj === 'poles' && wzrost > 0) return { value: Math.round((wzrost * 0.68) / 5) * 5, unit: 'cm' };
  if (rodzaj === 'boots' && Number(a.shoe_size) > 0) return { value: Number(a.shoe_size), unit: 'EU' };
  if (rodzaj === 'helmet' && Number(a.head) > 0) return { value: Number(a.head), unit: 'cm' };
  return null;
}

/** Rozmiar sztuki jako liczba albo zakres `{ od, do }`; null, gdy to nie liczba (np. „M”). */
export function rozmiarSztuki(size) {
  const s = String(size ?? '').trim().replace(',', '.');
  const zakres = s.match(/^(\d+(?:\.\d+)?)\s*[-–]\s*(\d+(?:\.\d+)?)/);
  if (zakres) return { od: Number(zakres[1]), do: Number(zakres[2]) };
  const liczba = s.match(/^(\d+(?:\.\d+)?)/);
  return liczba ? { od: Number(liczba[1]), do: Number(liczba[1]) } : null;
}

const TOLERANCJA = { ski: 5, snowboard: 4, poles: 5, boots: 0.5, helmet: 1 };

/** Odległość sztuki od celu (0 = idealnie) albo null, gdy nie da się porównać. */
export function odleglosc(rodzaj, cel, size) {
  const r = rozmiarSztuki(size);
  if (!cel || !r) return null;
  let { od, do: doR } = r;
  let wartosc = cel.value;
  // Buty w mondopoint (rozmiary poniżej 35): klient podaje EU, więc przeliczamy cel.
  if (rodzaj === 'boots' && doR < 35) wartosc = Math.round((cel.value * 2) / 3 - 1);
  if (rodzaj === 'boots' && doR < 35) { od = Math.floor(od); doR = Math.ceil(doR); }
  if (wartosc >= od && wartosc <= doR) return 0;
  return Math.min(Math.abs(wartosc - od), Math.abs(wartosc - doR));
}

// Kolejność stopni dopasowania: „pasuje”, „blisko”, reszta.
const STOPIEN = { best: 0, near: 1 };

/**
 * Kandydaci do sztuki zapytania w kolejności: najpierw „pasuje”, potem „blisko”, potem reszta;
 * w każdym stopniu najpierw zamówiona klasa, potem najbliższy rozmiar (28.10.2026). Do tej pory
 * klasa szła przed rozmiarem, więc gdy w zamówionej klasie nie było rozmiaru klienta (Junior do
 * EU 36, klient EU 42), górę listy zajmowały sztuki bez oznaczenia, a pasujące z innej klasy
 * stały za nimi. Bez wymiarów klienta nic nie ma oznaczenia i decyduje klasa, jak dotąd.
 * Zwraca kopie z polami `fit` ('best' | 'near' | null) i `distance`.
 */
export function ulozKandydatow(kandydaci, unit) {
  const rodzaj = rodzajSprzetu(unit && unit.type_name);
  const cel = docelowyRozmiar(rodzaj, unit && unit.answers);
  const tol = rodzaj ? TOLERANCJA[rodzaj] : 0;
  return (kandydaci || [])
    .filter((e) => e.type_id === unit.type_id)
    .map((e) => {
      const d = cel ? odleglosc(rodzaj, cel, e.size) : null;
      const fit = d == null ? null : (d <= tol ? 'best' : (d <= tol * 3 ? 'near' : null));
      return { ...e, distance: d, fit };
    })
    .sort((a, b) => {
      const stopien = (STOPIEN[a.fit] ?? 2) - (STOPIEN[b.fit] ?? 2);
      if (stopien) return stopien;
      const klasa = (b.class_id === unit.class_id) - (a.class_id === unit.class_id);
      if (klasa) return klasa;
      const da = a.distance == null ? Infinity : a.distance;
      const db = b.distance == null ? Infinity : b.distance;
      if (da !== db) return da - db;
      return String(a.name).localeCompare(String(b.name), 'pl') || a.id - b.id;
    });
}

/** Opis celu do pokazania pracownikowi: „ok. 160 cm”, „EU 42,5”. */
export function opisCelu(unit) {
  const cel = docelowyRozmiar(rodzajSprzetu(unit && unit.type_name), unit && unit.answers);
  if (!cel) return '';
  const liczba = String(cel.value).replace('.', ',');
  return cel.unit === 'EU' ? `EU ${liczba}` : `ok. ${liczba} ${cel.unit}`;
}
